/**
 * anlage-ballsets — der Bericht der Nachkontrolle: was der Lauf versprochen hat und was
 * wirklich in Plenty steht.
 *
 * Der Block entscheidet nichts und holt nichts. Er ist REINE ANZEIGE: kein eigener Abruf,
 * kein Polling, kein Datenzustand — den Bericht bekommt er von aussen, genau wie
 * `ErgebnisBlock` sein `ergebnis` bekommt, und den laufenden Job verfolgt die aufrufende
 * Seite mit dem Job-Hook des Core. Das einzige `useState` hier haelt die Befundliste auf
 * oder zu; das ist Darstellung, kein Zustand ueber die Welt.
 *
 * DER BLOCK DARF AN FEHLENDEN FELDERN NICHT ZERBRECHEN. Er steht an zwei Stellen: im
 * Ergebnis direkt nach dem Lauf (`resultSummary` ist zur Laufzeit ein `Record<string, any>`)
 * und in der Historie, wo ein Lauf aus der Zeit VOR dieser Phase gar keinen Bericht hat.
 * Jede Liste laeuft deshalb ueber `?? []`, und ein fehlender Bericht ergibt `null` — keine
 * leere Karte: eine leere Karte behauptet, da fehle etwas.
 *
 * VIER AUSGAENGE, und die Unterscheidung der letzten beiden ist der Punkt der Sache:
 *   kein Bericht      → nichts. Der Lauf hatte keinen Artikel zurueckzulesen.
 *   `ok`              → gruen. „Keine Abweichungen" — mit der Zahl der Pruefpunkte daneben,
 *                       denn „keine Abweichungen bei 3 Punkten" ist etwas anderes als bei 60.
 *   `abweichungen`    → gelb, mit der Tabelle. Schrift `#1A1A1A`, NIE weiss auf Gelb (E43).
 *   `nicht_moeglich`  → grau. Das ist KEIN Urteil ueber den Artikel, sondern ueber die
 *                       Leitung — deshalb weder gruen noch gelb.
 *
 * SOLL UND IST STEHEN IMMER BEIDE DA. Eine „Differenz" allein zwingt zum Nachschlagen in
 * Plenty, und genau das soll diese Phase ersparen.
 *
 * Der Knopf „Erneut prüfen" steht seit 06-07 in der GRAUEN und in der GELBEN Box — und
 * bewusst NICHT in der gruenen: dort gibt es nichts zu wiederholen. Er erscheint nur, wenn
 * die aufrufende Seite einen Callback mitgibt; der Block bleibt zustandslos und ruft ihn
 * bloss. Wer den Job verfolgt, entscheidet die Seite (Ergebnisblock: der Job-Hook des Core;
 * Historie: ein schlichter Aufruf, der danach den Lauf neu laedt).
 *
 * Das Farbschema der Badges kommt aus `Historie.tsx` (`nachkontrolleBadgeFarbe`) — dieselben
 * Status duerfen nicht zweimal verschieden eingefaerbt sein.
 */
import { Fragment, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, HelpCircle, RefreshCw } from 'lucide-react';
import { eur, zahl, zeitpunkt } from '../formate';
import type { Abweichung, AbweichungBereich, Befund, Nachkontrolle, Wert } from '../typen';

interface Props {
  /** Fehlt oder `null` → der Block zeigt nichts (der Lauf hatte nichts zurueckzulesen). */
  nachkontrolle?: Nachkontrolle | null;
  /** Gesetzt → der Knopf „Erneut prüfen" erscheint (grau/gelb). Fehlt er, gibt es keinen. */
  onErneutPruefen?: () => void;
  /** Laeuft gerade eine Pruefung? Dann ist der Knopf gesperrt und heisst „Prüft …". */
  laeuft?: boolean;
}

/**
 * Ein Einzelwert der Tabelle, lesbar gemacht.
 *
 * DAS FORMATIEREN PASSIERT HIER UND NICHT IM BACKEND, weil der Bericht als JSONB in der
 * Datenbank liegt: dort gehoert eine Zahl hin, keine gerenderte Zeichenkette. Eine
 * gespeicherte „25,50 €" liesse sich spaeter weder vergleichen noch umrechnen, und eine
 * Instanz mit anderer Spracheinstellung schriebe etwas anderes in dieselbe Spalte.
 *
 * `null`/`undefined` wird zum Strich — „nicht vorhanden" und „0" sind zwei verschiedene
 * Aussagen, und die zweite waere hier eine Erfindung.
 */
function wert(feld: string, v: Wert | undefined): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (typeof v === 'number') {
    const f = (feld ?? '').toLowerCase();
    return f.startsWith('preis') || f.startsWith('ek') ? eur(v) : zahl(v);
  }
  return v;
}

/** Die beiden Bereiche in Anzeige-Reihenfolge: `kern` zuerst, denn er blockiert den Verkauf. */
const BEREICHE: AbweichungBereich[] = ['kern', 'kopie'];

const BEREICH_TITEL: Record<AbweichungBereich, string> = {
  kern: 'Kern-Abweichungen',
  kopie: 'Kopie-Abweichungen',
};

/**
 * Kern kraeftiger als Kopie — dieselbe Idee wie `schrittFarbe` in `Historie.tsx`: der
 * wichtigere Zustand ist der auffaelligere. Eine fehlende Eigenschaft zieht man in Plenty in
 * einer Minute nach, ein fehlendes Paket macht den Artikel unverkaeuflich.
 */
const bereichFarbe = (bereich: AbweichungBereich) =>
  bereich === 'kern' ? 'text-[#1A1A1A] font-semibold' : 'text-gray-600';

/** Eine Zeile der Tabelle — Soll und Ist stehen IMMER beide da, nie eine Differenz allein. */
function AbweichungsZeile({ a }: { a: Abweichung }) {
  return (
    <tr className="border-b border-[#DAD9D9] last:border-0 align-top">
      <td className="px-2 py-1.5 break-words">{a.bezug}</td>
      <td className="px-2 py-1.5 font-mono text-gray-600 whitespace-nowrap">{a.feld}</td>
      <td className="px-2 py-1.5 whitespace-nowrap">{wert(a.feld, a.soll)}</td>
      <td className="px-2 py-1.5 whitespace-nowrap text-red-800">{wert(a.feld, a.ist)}</td>
      <td className="px-2 py-1.5 text-gray-600 break-words">{a.hinweis ?? ''}</td>
    </tr>
  );
}

/**
 * Die Abweichungstabelle — nach Bereich gruppiert, `kern` zuerst.
 *
 * EINE Tabelle und nicht zwei: die Spalten sollen untereinander stehen, damit sich Soll und
 * Ist ueber alle Zeilen hinweg vergleichen lassen. Die Gruppen sind deshalb Zwischenzeilen,
 * keine eigenen Tabellen — dieselbe Idee wie `GruppierteSchritte` in `ErgebnisBlock.tsx`.
 *
 * Tabellenkopf `bg-[#1A1A1A] text-white` ist die Konvention DIESES Programms
 * (`SetVariantenTabelle`, `Historie.tsx`) und die einzige Stelle mit weisser Schrift.
 */
function AbweichungsTabelle({ abweichungen }: { abweichungen: Abweichung[] }) {
  const gruppen = BEREICHE.map((bereich) => ({
    bereich,
    zeilen: abweichungen.filter((a) => a.bereich === bereich),
  })).filter((g) => g.zeilen.length > 0);

  // Ein unbekannter Bereich aus einem aelteren Bericht faellt sonst still unter den Tisch —
  // er bekommt keine Ueberschrift, aber er wird gezeigt.
  const uebrige = abweichungen.filter((a) => !BEREICHE.includes(a.bereich));

  return (
    <div className="overflow-x-auto border border-yellow-300 rounded bg-white">
      <table className="w-full text-xs">
        <thead className="bg-[#1A1A1A] text-white">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Bezug</th>
            <th className="px-2 py-1.5 text-left font-medium">Feld</th>
            <th className="px-2 py-1.5 text-left font-medium">Soll</th>
            <th className="px-2 py-1.5 text-left font-medium">Ist</th>
            <th className="px-2 py-1.5 text-left font-medium">Hinweis</th>
          </tr>
        </thead>
        <tbody>
          {gruppen.map((g) => (
            // Das Listenelement ist die Gruppe (Ueberschrift + ihre Zeilen) — der `key`
            // gehoert deshalb an das Fragment, nicht an das erste `<tr>`.
            <Fragment key={g.bereich}>
              <tr className="bg-gray-50 border-b border-[#DAD9D9]">
                <td colSpan={5} className={`px-2 py-1 text-[11px] ${bereichFarbe(g.bereich)}`}>
                  {zahl(g.zeilen.length)} {BEREICH_TITEL[g.bereich]}
                </td>
              </tr>
              {g.zeilen.map((a, i) => (
                <AbweichungsZeile key={`${g.bereich}-${a.bezug}-${a.feld}-${i}`} a={a} />
              ))}
            </Fragment>
          ))}
          {uebrige.map((a, i) => (
            <AbweichungsZeile key={`uebrig-${a.bezug}-${a.feld}-${i}`} a={a} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Die Befunde — einklappbar, standardmaessig zu.
 *
 * Sie sind keine Maengelliste: ein Befund entsteht auch dann, wenn alles stimmt. Er ist die
 * beobachtete Antwort auf eine Frage, die dieses Projekt seit Phase 3 offen mit sich traegt
 * („uebernimmt Plenty `bundleType` aus dem POST-Body?"). Deshalb steht er neben den
 * Abweichungen und nicht unter ihnen — und deshalb ist die Liste zu, bis jemand sie aufmacht.
 */
function Befundliste({ befunde }: { befunde: Befund[] }) {
  const [offen, setOffen] = useState(false);
  if (befunde.length === 0) return null;

  return (
    <div className="border border-[#DAD9D9] rounded bg-white">
      <button
        type="button"
        onClick={() => setOffen((b) => !b)}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left text-xs font-semibold text-[#1A1A1A] hover:bg-gray-50"
      >
        {offen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <HelpCircle size={12} className="text-gray-500" />
        Erkenntnisse ({zahl(befunde.length)})
      </button>

      {offen && (
        <div className="px-3 pb-2 space-y-1">
          <p className="text-[11px] text-gray-500 italic">
            Beobachtete Antworten auf offene Fragen dieses Programms — keine Fehler. Was hier
            steht, gehört nach dem Lauf in die Dokumentation, nicht in Plenty nachgezogen.
          </p>
          {befunde.map((b, i) => (
            <div key={`${b.thema}-${i}`} className="flex items-baseline gap-2 text-[11px]">
              <span className="w-40 shrink-0 font-mono text-gray-500">{b.thema}</span>
              <span
                className={`break-words ${
                  // Eine Warnung ist der einzige Befund, der eine Handlung nahelegt — sie
                  // wird deshalb hervorgehoben, aber nicht rot: rot ist der Fehlerfall.
                  b.text?.startsWith('Warnung:') ? 'text-yellow-900' : 'text-gray-700'
                }`}
              >
                {b.text}
                {b.bezug && <span className="text-gray-500"> ({b.bezug})</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Der Knopf „Erneut prüfen" — in der grauen und der gelben Box.
 *
 * NICHT in der gruenen: ein Bericht ohne Abweichungen hat nichts zu wiederholen, und ein
 * Knopf, der dort steht, lockt zu einem Lauf, der sechs Plenty-Lesevorgaenge kostet und
 * dasselbe sagen wird.
 *
 * Der Block bleibt zustandslos: kein eigener Abruf, kein Polling, kein Wissen darueber, was
 * der Callback anstellt. Ob dahinter ein Job-Hook (Ergebnisblock) oder ein schlichter
 * Aufruf mit Nachladen (Historie) steckt, entscheidet die aufrufende Seite.
 */
function ErneutPruefenKnopf({ onErneutPruefen, laeuft }: Pick<Props, 'onErneutPruefen' | 'laeuft'>) {
  if (!onErneutPruefen) return null;
  return (
    <button
      type="button"
      onClick={onErneutPruefen}
      disabled={laeuft === true}
      className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[11px] font-medium bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <RefreshCw size={12} /> {laeuft ? 'Prüft …' : 'Erneut prüfen'}
    </button>
  );
}

/** Zeitpunkt, Prüfpunkte und Lesevorgänge — in allen drei Boxen dieselbe Zeile. */
function Fusszeile({ nachkontrolle }: { nachkontrolle: Nachkontrolle }) {
  return (
    <p className="text-[11px] opacity-80">
      Geprüft am {zeitpunkt(nachkontrolle.geprueftAm)}
      {' · '}
      {zahl(nachkontrolle.geprueftePunkte ?? 0)} Prüfpunkte
      {' · '}
      {zahl(nachkontrolle.lesevorgaenge ?? 0)} Lesevorgänge
    </p>
  );
}

export default function NachkontrolleBlock({ nachkontrolle, onErneutPruefen, laeuft }: Props) {
  // Kein Bericht → gar nichts. Ein Lauf ohne Artikel hat nichts zu pruefen, und eine leere
  // Karte behauptet, da fehle etwas.
  if (!nachkontrolle) return null;

  const abweichungen = nachkontrolle.abweichungen ?? [];
  const befunde = nachkontrolle.befunde ?? [];
  const kern = abweichungen.filter((a) => a.bereich === 'kern').length;
  const kopie = abweichungen.filter((a) => a.bereich === 'kopie').length;

  // ---------- Nicht moeglich ----------
  // Grau, nicht rot: gescheitert ist das LESEN, nicht der Artikel. Wer das rot faerbt,
  // schickt jemanden los, einen Artikel zu reparieren, der in Ordnung sein kann.
  if (nachkontrolle.status === 'nicht_moeglich') {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 text-gray-800 px-3 py-2 space-y-1">
        <h3 className="text-xs font-bold flex items-center gap-1.5">
          <HelpCircle size={14} /> Nachkontrolle nicht möglich
        </h3>
        <p className="text-[11px] break-words">
          {nachkontrolle.grund ?? 'Kein Grund übermittelt — Einzelheiten stehen im Server-Log.'}
        </p>
        <p className="text-[11px]">
          Der Artikel ist davon <strong>unberührt</strong>: die Nachkontrolle liest nur und
          ändert nichts. Die Prüfung kann später wiederholt werden.
        </p>
        <ErneutPruefenKnopf onErneutPruefen={onErneutPruefen} laeuft={laeuft} />
        <Fusszeile nachkontrolle={nachkontrolle} />
        <Befundliste befunde={befunde} />
      </div>
    );
  }

  // ---------- Abweichungen ----------
  // Gelb mit Schrift #1A1A1A — nie weisse Schrift auf Gelb (E43).
  if (nachkontrolle.status === 'abweichungen' || abweichungen.length > 0) {
    return (
      <div className="rounded border border-yellow-300 bg-yellow-50 text-[#1A1A1A] px-3 py-2 space-y-2">
        <h3 className="text-xs font-bold flex items-center gap-1.5">
          <AlertTriangle size={14} /> Nachkontrolle: {zahl(abweichungen.length)}{' '}
          {abweichungen.length === 1 ? 'Abweichung' : 'Abweichungen'} ({zahl(kern)} Kern,{' '}
          {zahl(kopie)} Kopie)
        </h3>
        <p className="text-[11px]">
          Der Artikel steht <strong>inaktiv</strong> in Plenty. Die folgenden Punkte weichen
          von dem ab, was der Lauf angelegt hat — vor dem Freischalten prüfen und in Plenty
          nachziehen.
        </p>
        <AbweichungsTabelle abweichungen={abweichungen} />
        {/* Nach dem Nachziehen in Plenty: noch einmal pruefen, statt ein zweites Ballset
            anzulegen (genau die Falle, die der Doppelanlage-Schutz verhindern soll). */}
        <ErneutPruefenKnopf onErneutPruefen={onErneutPruefen} laeuft={laeuft} />
        <Fusszeile nachkontrolle={nachkontrolle} />
        <Befundliste befunde={befunde} />
      </div>
    );
  }

  // ---------- Keine Abweichungen ----------
  return (
    <div className="rounded border border-green-200 bg-green-50 text-green-900 px-3 py-2 space-y-1">
      <h3 className="text-xs font-bold flex items-center gap-1.5">
        <CheckCircle2 size={14} /> Nachkontrolle: keine Abweichungen
      </h3>
      <p className="text-[11px]">
        {zahl(nachkontrolle.geprueftePunkte ?? 0)} Prüfpunkte,{' '}
        {zahl(nachkontrolle.lesevorgaenge ?? 0)} Lesevorgänge — alles, was der Lauf angelegt
        hat, steht so in Plenty.
      </p>
      <Fusszeile nachkontrolle={nachkontrolle} />
      <Befundliste befunde={befunde} />
    </div>
  );
}
